import { InlineError } from "@/components/QueryState";
import { authClient } from "@/lib/auth-client";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export function VerifyPage() {
  useDocumentTitle("Verify email");
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { email?: string; otp?: string };
  const [signInError, setSignInError] = useState<string | null>(null);
  const ranRef = useRef(false);
  const { email, otp } = search;
  const error = !email || !otp ? "This sign-in link is missing required information." : signInError;

  useEffect(() => {
    if (ranRef.current || !email || !otp) return;
    ranRef.current = true;

    const name = sessionStorage.getItem(`signup-name:${email.toLowerCase()}`) ?? undefined;

    authClient.signIn
      .emailOtp({ email, otp, name })
      .then(({ error: authError }) => {
        if (authError) {
          setSignInError(authError.message ?? "This link is invalid or has expired.");
          return;
        }
        if (name) sessionStorage.removeItem(`signup-name:${email.toLowerCase()}`);
        navigate({ to: "/" });
      })
      .catch((err: unknown) => {
        setSignInError(err instanceof Error ? err.message : "Sign in failed.");
      });
  }, [navigate, email, otp]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-8 border rounded-xl bg-card shadow-sm text-center">
        <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">Boo</p>
        {error === null ? (
          <>
            <h1 className="text-2xl font-semibold">Signing you in…</h1>
            <p className="text-sm text-muted-foreground">Hang tight, this should only take a moment.</p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">Couldn't sign you in</h1>
            <InlineError error={error} />
            <Link
              to="/login"
              className="inline-block text-sm underline underline-offset-4 hover:text-foreground"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
